import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getReport, client } from "@/lib/api";
import { money, moneyFull, pct, months, num } from "@/lib/format";
import { ArrowLeft, Copy, Printer, AlertTriangle, CheckCircle2, XCircle, Mail, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import BenefitBreakdownChart from "@/components/BenefitBreakdownChart";
import SensitivityChart from "@/components/SensitivityChart";
import LanguageToggle from "@/components/LanguageToggle";
import AppFooter from "@/components/AppFooter";
import FeedbackCard from "@/components/FeedbackCard";
import { useI18n } from "@/lib/i18n";

const BENEFIT_HARDNESS = {
  traffic_value: { tag: "HARD", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  lead_value: { tag: "HARD", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  revenue_value: { tag: "HARD", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  seo_value: { tag: "SOFT", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  brand_value: { tag: "SOFT", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  risk_reduction_value: { tag: "SOFT", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const REC_STYLE = {
  APPROVE: { label: "Approve", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  REVISE:  { label: "Revise",  cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle },
  REJECT:  { label: "Reject",  cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};

export default function Report() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState({});
  const [fx, setFx] = useState({ as_of: null, source: null, live: false });
  const [deliverEmail, setDeliverEmail] = useState("");
  const [delivered, setDelivered] = useState(null);
  const [delivering, setDelivering] = useState(false);

  useEffect(() => {
    getReport(slug).then(setReport).catch((e) => setError(e?.response?.data?.detail || "Report not found"));
    client.get("/benchmarks/sources").then((r) => setSources(r.data?.sources || {})).catch(() => {});
    client.get("/currency/rates").then((r) => setFx({ as_of: r.data?.as_of, source: r.data?.source, live: !!r.data?.live })).catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="max-w-md text-center p-10 border border-slate-200">
          <h2 className="font-heading text-2xl text-slate-900 mb-3">Report unavailable</h2>
          <p className="text-slate-600 text-sm mb-6">{error}</p>
          <Link to="/calculator">
            <Button className="bg-slate-900 text-white rounded-sm">Start a new analysis</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="text-slate-500 text-sm">Loading report…</div>
      </div>
    );
  }

  const base = report.results.base;
  const rec = REC_STYLE[report.executive_summary?.recommendation] || REC_STYLE.REVISE;
  const RecIcon = rec.Icon;
  const roiPositive = base.roi_pct >= 0;

  function copyLink() {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t("report.copy") + " ✓");
  }

  async function sendEmail() {
    if (!deliverEmail || !deliverEmail.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setDelivering(true);
    try {
      const r = await client.post(`/reports/${slug}/deliver`, { email: deliverEmail });
      setDelivered(r.data);
      toast.success(`Recorded for ${r.data.delivered_to}`, {
        description: "Report link is live — inbox delivery available on request.",
      });
    } catch (e) {
      toast.error("Could not send", { description: e?.message });
    } finally {
      setDelivering(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] print-page">
      <header className="border-b border-slate-200 bg-white no-print sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.24em] font-semibold">PressImpact</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Button data-testid="copy-link-btn" variant="outline" onClick={copyLink} className="rounded-sm border-slate-200">
              <Copy className="h-4 w-4 mr-2" /> {t("report.copy")}
            </Button>
            <Button data-testid="print-btn" variant="outline" onClick={() => window.print()} className="rounded-sm border-slate-200">
              <Printer className="h-4 w-4 mr-2" /> {t("report.print")}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        {/* Hero header */}
        <section className="print-break-avoid">
          <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-3">
            {report.model_type.toUpperCase()} · {report.industry} · {report.wire_tier} wire
          </p>
          <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tight text-slate-900">
            {report.report_name || "Untitled campaign"}
          </h1>
          <p className="text-sm text-slate-500 mt-3">
            Generated {new Date(report.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })} · Expires {new Date(report.expires_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
          </p>
        </section>

        {report.nordic_fallback && (
          <section
            data-testid="nordic-fallback-banner"
            className="border border-amber-200 bg-amber-50 px-5 py-3 flex items-start gap-3 print-break-avoid"
          >
            <Info className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">{t("report.nordic_fallback")}</p>
          </section>
        )}

        {/* LAYER 1 — CMO SCORECARD */}
        <section className="print-break-avoid" data-testid="cmo-scorecard">
          <div className="flex items-baseline justify-between mb-4">
            <p className="text-xs tracking-[0.28em] uppercase text-slate-500">{t("report.layer1")}</p>
            <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400">{t("report.layer1.sub")}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            <Scorecard
              label={t("report.scorecard.cost")}
              value={`${(base.scorecard?.cost_efficiency ?? 0).toFixed(2)}×`}
              interpretation={
                base.scorecard?.cost_efficiency >= 1
                  ? `Every ${report.currency} spent returns ${base.scorecard.cost_efficiency.toFixed(2)} in modelled benefit.`
                  : `Spend outpaces returns — every ${report.currency} recovers only ${base.scorecard.cost_efficiency.toFixed(2)}.`
              }
              tone={base.scorecard?.cost_efficiency >= 1 ? "pos" : "neg"}
              testid="scorecard-cost-efficiency"
            />
            <Scorecard
              label={t("report.scorecard.sov")}
              value={`${(base.scorecard?.share_of_voice_index ?? 0).toFixed(2)}×`}
              interpretation={`Delivered earned reach equals ${(base.scorecard?.share_of_voice_index ?? 0).toFixed(2)}× your monthly organic traffic.`}
              testid="scorecard-share-of-voice"
            />
            <Scorecard
              label={t("report.scorecard.reach")}
              value={reachShort(base.scorecard?.media_reach)}
              interpretation={`Estimated ${num(base.scorecard?.media_reach)} impressions delivered after pickup weighting.`}
              testid="scorecard-media-reach"
            />
          </div>
        </section>

        {/* LAYER 2 — CFO DETAIL divider */}
        <section className="print-break-avoid pt-4 border-t border-slate-200">
          <div className="flex items-baseline justify-between">
            <p className="text-xs tracking-[0.28em] uppercase text-slate-500">{t("report.layer2")}</p>
            <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400">{t("report.layer2.sub")}</span>
          </div>
        </section>

        {/* KPI band */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 print-break-avoid" data-testid="report-kpi-band">
          <Kpi label="ROI" value={pct(base.roi_pct)} valueClass={roiPositive ? "text-emerald-600" : "text-red-600"} />
          <Kpi label="Net Benefit" value={moneyFull(base.net_benefit, report.currency)} valueClass={base.net_benefit >= 0 ? "text-slate-900" : "text-red-600"} />
          <Kpi label="Payback" value={base.payback_beyond_timeframe ? `Beyond ${report.inputs.timeframe_months} mo` : months(base.payback_months)} />
          <Kpi label="Break-even leads" value={num(base.break_even_leads)} />
        </section>

        {/* Executive Summary */}
        <section className="border border-slate-200 bg-white p-8 print-break-avoid">
          <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
            <div>
              <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-2">Executive Summary</p>
              <h2 className="font-heading text-2xl font-medium text-slate-900">Recommendation</h2>
            </div>
            <div className={`inline-flex items-center gap-2 border ${rec.cls} px-4 py-2 text-sm font-semibold`} data-testid="recommendation-badge">
              <RecIcon className="h-4 w-4" />
              {rec.label.toUpperCase()}
            </div>
          </div>
          <p className="text-slate-700 leading-relaxed text-base" data-testid="exec-summary-text">
            {report.executive_summary?.summary}
          </p>
          {report.executive_summary?.rationale && (
            <p className="text-slate-500 text-sm mt-4 border-l-2 border-slate-200 pl-4">
              {report.executive_summary.rationale}
            </p>
          )}
          <p className="mt-4 text-[10px] tracking-[0.2em] uppercase text-slate-400">
            {friendlyEngineLabel(report.executive_summary?.generated_by)}
          </p>
        </section>

        {/* Benefit breakdown */}
        <section className="border border-slate-200 bg-white p-8 print-break-avoid">
          <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">Benefit Breakdown</p>
          <h2 className="font-heading text-2xl font-medium text-slate-900 mb-6">Where the value comes from</h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <BenefitBreakdownChart benefits={base.benefits} currency={report.currency} />
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(base.benefits).map(([k, v]) => {
                  const h = BENEFIT_HARDNESS[k] || { tag: "SOFT", cls: "bg-amber-50 text-amber-700 border-amber-200" };
                  return (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-3 text-slate-600 capitalize">
                        <div className="flex items-center gap-2">
                          <span>{k.replace(/_/g, " ")}</span>
                          <span className={`text-[9px] tracking-[0.15em] font-semibold border px-1.5 py-0.5 ${h.cls}`}>{h.tag}</span>
                        </div>
                      </td>
                      <td className="py-3 text-right font-mono text-slate-900">{moneyFull(v, report.currency)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td className="py-3 font-semibold text-slate-900">Total benefit</td>
                  <td className="py-3 text-right font-mono font-semibold text-slate-900">{moneyFull(base.total_benefit, report.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Sensitivity */}
        <section className="border border-slate-200 bg-white p-8 print-break-avoid">
          <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">Sensitivity Analysis</p>
          <h2 className="font-heading text-2xl font-medium text-slate-900 mb-6">Conservative → Base → Optimistic</h2>
          <SensitivityChart results={report.results} currency={report.currency} />
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {["conservative", "base", "optimistic"].map((k) => (
              <div key={k} className="border border-slate-200 p-4">
                <div className="text-xs tracking-[0.2em] uppercase text-slate-500">{k}</div>
                <div className={`font-heading text-3xl font-light mt-2 ${report.results[k].roi_pct >= 0 ? "text-slate-900" : "text-red-600"}`}>
                  {pct(report.results[k].roi_pct)}
                </div>
                <div className="text-xs text-slate-500 mt-1 mono">
                  Net {moneyFull(report.results[k].net_benefit, report.currency)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Assumptions */}
        <section className="border border-slate-200 bg-white p-8 print-break-avoid">
          <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">Assumptions</p>
          <h2 className="font-heading text-2xl font-medium text-slate-900 mb-6">All inputs & benchmarks used</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 text-xs uppercase tracking-[0.2em] text-slate-500">
                  <th className="py-3 pr-4">Field</th>
                  <th className="py-3 pr-4">Value</th>
                  <th className="py-3 pr-4">Origin</th>
                  <th className="py-3">Source</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(report.benchmarks).map(([k, v]) => (
                  <tr key={k} className="border-b border-slate-100">
                    <td className="py-3 pr-4 text-slate-600 capitalize">{k.replace(/_/g, " ")}</td>
                    <td className="py-3 pr-4 mono text-slate-900">{typeof v === "number" ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }) : String(v)}</td>
                    <td className="py-3 pr-4 text-xs">
                      {report.benchmark_flags?.[k] ? (
                        <span className="text-slate-700">User override</span>
                      ) : (
                        <span className="text-amber-700">Estimated</span>
                      )}
                    </td>
                    <td className="py-3 text-xs text-slate-500">
                      {(() => {
                        const s = sources[k];
                        if (!s) return "—";
                        if (typeof s === "string") return s;
                        const licence = s.licence === "pending" ? (
                          <span data-testid={`licence-${k}`} className="ml-2 text-[9px] tracking-[0.15em] font-semibold border px-1.5 py-0.5 bg-amber-50 text-amber-700 border-amber-200">
                            {t("licence.pending").toUpperCase()}
                          </span>
                        ) : (
                          <span className="ml-2 text-[9px] tracking-[0.15em] font-semibold border px-1.5 py-0.5 bg-slate-100 text-slate-700 border-slate-200">
                            {t("licence.public").toUpperCase()}
                          </span>
                        );
                        return (
                          <span>
                            {s.source}
                            {s.date && <span className="text-slate-400 ml-1">· {s.date}</span>}
                            {licence}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Drivers & sensitivity summary */}
        <section className="grid md:grid-cols-2 gap-8 print-break-avoid">
          <div className="border border-slate-200 bg-white p-8">
            <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">Top 3 value drivers</p>
            <ol className="space-y-3">
              {report.results.top_value_drivers.map((d, i) => (
                <li key={d.key} className="flex justify-between items-baseline border-b border-slate-100 pb-2">
                  <span className="text-slate-700">{i + 1}. {d.label}</span>
                  <span className="mono text-slate-900">{moneyFull(d.value, report.currency)}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="border border-slate-200 bg-white p-8">
            <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">Most sensitive assumptions</p>
            <ol className="space-y-3">
              {report.results.top_sensitive_assumptions.map((s, i) => (
                <li key={s.key} className="border-b border-slate-100 pb-2">
                  <div className="flex justify-between">
                    <span className="text-slate-700">{i + 1}. {s.label}</span>
                    <span className="mono text-slate-900">±{s.swing.toFixed(0)}%</span>
                  </div>
                  <div className="text-xs text-slate-500 mono mt-1">
                    Low {pct(s.low_roi_pct)} → High {pct(s.high_roi_pct)}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Email delivery card (mocked transport) */}
        <section className="border border-slate-200 bg-white p-8 print-break-avoid no-print" data-testid="deliver-section">
          <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">{t("report.email")}</p>
          {delivered ? (
            <div className="border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Recorded for delivery to <strong>{delivered.delivered_to}</strong> at{" "}
              {new Date(delivered.delivered_at).toLocaleString()}. The report link above
              is already live — inbox delivery is enabled for early customers on request.
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="deliver-email-input"
                  type="email"
                  placeholder="stakeholder@company.com"
                  value={deliverEmail}
                  onChange={(e) => setDeliverEmail(e.target.value)}
                  className="pl-9 rounded-sm border-slate-200"
                />
              </div>
              <Button
                data-testid="deliver-send-btn"
                onClick={sendEmail}
                disabled={delivering}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
              >
                {delivering ? "Sending…" : t("report.email")}
              </Button>
            </div>
          )}
        </section>

        {/* Feedback (one-click, cohort validation) */}
        <FeedbackCard slug={slug} />

        <footer className="text-xs text-slate-400 tracking-[0.2em] uppercase pt-6 border-t border-slate-200 flex justify-between flex-wrap gap-2">
          <span>Report ID {report.slug}</span>
          <span data-testid="report-fx-source">
            {fx.as_of ? (
              <>FX · ECB {fx.live ? "live" : "cached"} · {new Date(fx.as_of).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</>
            ) : "Approx · ECB reference rates"}
          </span>
        </footer>
      </main>
      <AppFooter />
    </div>
  );
}

function friendlyEngineLabel(key) {
  const map = {
    "claude-sonnet-5": "Written by AI narrative engine",
    "cohort-seed": "Rules-based summary (seed data)",
    "rules-fallback": "Rules-based summary",
  };
  if (!key) return "Written by AI narrative engine";
  if (map[key]) return map[key];
  if (key.startsWith("fallback")) return "Rules-based summary";
  return "Written by AI narrative engine";
}

function Kpi({ label, value, valueClass = "text-slate-900" }) {
  return (
    <div className="bg-white p-6">
      <div className="text-xs tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <div className={`font-heading text-3xl md:text-4xl font-light mt-3 tracking-tight ${valueClass}`}>
        {value}
      </div>
    </div>
  );
}

function Scorecard({ label, value, interpretation, tone = "neutral", testid }) {
  const valueClass = tone === "neg" ? "text-red-600" : tone === "pos" ? "text-emerald-600" : "text-slate-900";
  return (
    <div className="bg-white p-8" data-testid={testid}>
      <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold">{label}</div>
      <div className={`font-heading text-5xl md:text-6xl font-light mt-4 tracking-tight ${valueClass}`}>
        {value}
      </div>
      <p className="text-sm text-slate-600 mt-4 leading-relaxed">{interpretation}</p>
    </div>
  );
}

function reachShort(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}
